/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { DNode } from "@dojo/framework/core/interfaces";
import { CURVE_SELECTION_STROKE_WIDTH } from "../../Constants";
import { isClosedShape } from "../../store/SelectionHelper";
import { LineCap } from "../interfaces/model/enumerations/index";
import { IConnectionShape } from "../interfaces/model/PageObjects/ConnectionShape";
import { ISolidFillProperties } from "../interfaces/model/properties/FillProperties";
import { ILineProperties } from "../interfaces/model/properties/LineProperties";
import { getDefaultLineFill, getMaxDimension, getShapeFill } from "../util";
import MarkerObject from "./MarkerObject";
import { IPageObjectRenderProps } from "./PageObject";
import PageObject from "./PageObject";
import { ShapePath } from "./ShapePath";

/**
 * Connection shape renderer properties
 */
export interface IConnectionShapeRenderProps extends IPageObjectRenderProps<IConnectionShape> {
    path: string;
}

/**
 * Connection shape stateless renderer
 */
export class ConnectionShape extends PageObject<IConnectionShapeRenderProps> {
	/**
     * Marker head id
     */
	private markerHeadID = "";

	/**
     * Marker tail id
     */
	private markerTailID = "";

	/**
     * Add marker if line has headEnd and headTail properties
     * @return {MarkerObject | null}
     */
	protected renderDefs() {
		if (this.properties.pageObject.spPr.ln && this.properties.pageObject.allowsArrows) {
			const { headEnd, tailEnd } = this.properties.pageObject.spPr.ln;
			const isHead = (headEnd && headEnd !== LineCap.NO_ARROW);
			const isTail = (tailEnd && tailEnd !== LineCap.NO_ARROW);
			this.markerHeadID = isHead ? `${this.properties.pageObject.shapeId}_headID` : "";
			this.markerTailID = isTail ? `${this.properties.pageObject.shapeId}_tailID` : "";
			return (
				<MarkerObject
					lineProps={this.properties.pageObject.spPr.ln}
					markerHeadID={this.markerHeadID}
					markerHeight={12}
					markerTailID={this.markerTailID}
					markerWidth={12}
				/>
			);
		}
		return null;
	}

	/**
     * Render object.
     */
	protected renderObject() {
		const { pageObject, path } = this.properties;
		const { shapeId, spPr: { fill, ln } } = pageObject;
		// key for the path element
		const key = `path_${this.properties.key}`;
		const lineFill = ln && ln.fill as ISolidFillProperties || getDefaultLineFill();
		return (
			<g>
				<ShapePath
					fill={getShapeFill(shapeId, fill, this.imageFillUUID)}
					key={key}
					lineFill={lineFill}
					lineProps={ln}
					markerHeadID={this.markerHeadID}
					markerTailID={this.markerTailID}
					ondblclick={this._onShapeDblClick}
					onpointerdown={this._onShapePointerDown}
					onpointerup={this._onShapePointerUp}
					path={path}
					strokeSelect={isClosedShape(pageObject)}
				/>
				<path
					d={path}
					fill="none"
					key="curveShapeForSelection"
					ondblclick= {this._onShapeDblClick}
					onpointerdown={this._onShapePointerDown}
					pointer-events="stroke"
					stroke="none"
					stroke-width={CURVE_SELECTION_STROKE_WIDTH}
					vector-effect="non-scaling-stroke"
				/>
			</g>
		);
	}

	protected renderIfShapeTextBody(): DNode {
		return null;
	}

	protected getViewBox(): {height: number; left: number; top: number; width: number} {
		return {
			height: getMaxDimension(this.properties.pageObject.spPr.xfrm.ext.cy),
			left: 0,
			top: 0,
			width: getMaxDimension(this.properties.pageObject.spPr.xfrm.ext.cx),
		};
	}
};
